<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="(equipment.equipment_id ? 'Edit Equipment: ' + equipment.equipment_id : 'New Equipment') + ' :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0">
                    <li class="breadcrumb-item font-weight-light">
                        <router-link to="/equipment">
                            <i class="fa fa-shield"></i> Equipment
                        </router-link>
                    </li>
                    <template v-if="equipment.equipment_id">
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/equipment/view/${equipment.equipment_id}`">
                                {{ equipment.equipment_type_name }}
                                <span v-if="equipment.equipment_name">-</span> {{ equipment.equipment_name }}
                                <span v-if="equipment.serial_number">-</span> {{ equipment.serial_number }}
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active">Edit</li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item active">New Equipment</li>
                    </template>
                    <li class="ml-3">
                        <button type="button" class="btn btn-sm btn-success" v-on:click="saveEquipment(this)" :disabled="saving">
                            <span v-if="equipment.equipment_id">Update</span>
                            <span v-else>Create</span>
                            Equipment
                        </button>
                    </li>
                </ol>
            </h4>

            <div class="row">
                <div class="col-xl-8 col-lg-10 col-md-12">

                    <div v-if="types.length == 1" class="mb-3">
                        <div class="alert alert-danger">
                            Your account does not have any equipment types set up.
                            <template v-if="$store.state.user.is_admin">
                                <router-link to="/equipment#types">Click here</router-link> to set up your equipment types.
                            </template>
                            <template v-else>
                                Contact an account admin to set up equipment types.
                            </template>
                        </div>
                    </div>

                    <div v-if="missingEmployeeId" class="mb-3">
                        <div class="alert alert-danger">
                            Your User Profile is missing an associated Employee Record. This is required to create equipment.
                            <div>
                                Contact an account admin to correct your profile. This is the link they will need:

                                <div class="mt-2">
                                    <a :href="`/users/view/${$store.state.user.user_id}`">Edit Profile for {{ this.$store.state.user.first_name }} {{ this.$store.state.user.last_name }}</a>
                                </div>

                                <div class="mt-2">
                                    Account Admins: <span v-for="(admin, i) in admins"><span v-if="i > 0">, </span><a :href="`mailto:${admin.email}`">{{ admin.FirstName }} {{ admin.LastName }}</a></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header text-muted">Basic Information</div>
                        <div class="card-body">

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.equipment_type_id }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right" :class="{'text-danger': errors.equipment_type_id}">
                                    Equipment Type: <i class="fa fa-asterisk text-danger"></i>
                                </label>
                                <div class="col-sm-8">
                                    <b-form-select v-model="equipment.equipment_type_id" :options="types" :class="{'is-invalid': errors.equipment_type_id}"></b-form-select>
                                    <span v-if="errors.equipment_type_id" class="text-danger">
                                        <span v-for="error in errors.equipment_type_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.model_number }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right" :class="{'text-danger': errors.model_number}">Model#: <i class="fa fa-asterisk text-danger"></i></label>
                                <div class="col-sm-8">
                                    <b-form-input v-model="equipment.model_number" placeholder="Equipment Model Number" :class="{'is-invalid': errors.model_number}"></b-form-input>
                                    <span v-if="errors.model_number" class="text-danger">
                                        <span v-for="error in errors.model_number">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.serial_number }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right" :class="{'text-danger': errors.serial_number}">Serial#: <i class="fa fa-asterisk text-danger"></i></label>
                                <div class="col-sm-8">
                                    <b-form-input v-model="equipment.serial_number" placeholder="Equipment Serial Number" :class="{'is-invalid': errors.serial_number}"></b-form-input>
                                    <span v-if="errors.serial_number" class="text-danger">
                                        <span v-for="error in errors.serial_number">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.equipment_name }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right" :class="{'text-danger': errors.equipment_name}">Equipment Name:</label>
                                <div class="col-sm-8">
                                    <b-form-input v-model="equipment.equipment_name" placeholder="(Optional). Equipment Name" :class="{'is-invalid': errors.equipment_name}"></b-form-input>
                                    <div v-if="errors.equipment_name" class="text-danger">
                                        <span v-for="error in errors.equipment_name">{{ error }}</span>
                                    </div>
                                    <div class="alert alert-info small mt-3">Equipment Name can be any identifier used by your company. MeritEHS does not enforce any unique/pattern validation rules.</div>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.manufacturer }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right" :class="{'text-danger': errors.manufacturer}">Manufacturer:</label>
                                <div class="col-sm-8">
                                    <b-form-input v-model="equipment.manufacturer" placeholder="(Optional). Equipment Manufacturer" :class="{'is-invalid': errors.manufacturer}"></b-form-input>
                                    <div v-if="errors.manufacturer" class="text-danger">
                                        <span v-for="error in errors.manufacturer">{{ error }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Equipment tracking takes care of this information -->
                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.division_id }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right"">Division:</label>
                                <div class="col-sm-8">
                                    <template v-if="!equipment.equipment_id">
                                        <select v-model="equipment.division_id" :class="{'is-invalid': errors.division_id}" class="form-control">
                                            <option :value="null">
                                                -- Select a Division --
                                            </option>
                                            <option v-for="(div, i) in divisions" :value="div.DivisionId">
                                                <span v-if="div.division_code">{{ div.division_code }} - </span>{{ div.DivisionName }}
                                            </option>
                                        </select>
                                        <span v-if="errors.division_id" class="text-danger">
                                            <span v-for="error in errors.division_id">{{ error }}</span>
                                        </span>
                                    </template>
                                    <template v-else>
                                        <div class="alert alert-info small">
                                            Division changes on existing equipment are handled by the Equipment Tracking feature. You can issue/check-in equipment to change its division and/or jobsite.
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header text-muted">Equipment Description</div>
                        <div class="card-body">
                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.equipment_desc }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">Description:</label>
                                <div class="col-sm-8">
                                    <b-form-textarea v-model="equipment.equipment_desc" placeholder="(Optional). Enter Equipment Description." rows="3"></b-form-textarea>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Equipment tracking takes care of this information -->
                    <div class="card mb-3" v-if="!equipment.equipment_id || missingJobsite">
                        <div class="card-header text-muted">Assignee & Jobsite</div>
                        <div class="card-body">

                            <div v-bind:class="{ 'form-group row': true, 'has-error': errors.employee_id }">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">Assignee:</label>
                                <div class="col-sm-8">
                                    <template v-if="equipment.equipment_id">
                                        <router-link :to="`/employees/view/${equipment.equipment_id}`">{{ equipment.EmployeeNumber }} - {{ equipment.EmployeeFirstName }} {{ equipment.EmployeeLastName }}</router-link>
                                    </template>
                                    <template v-else-if="this.$root.hasPermission('equipment.manage')">
                                        <employee-select ref="employeeSelector" @vselected="onEmployeeSelect"></employee-select>
                                    </template>
                                    <template v-else>
                                        {{ $store.state.user.first_name }} {{ $store.state.user.last_name }}
                                        <input type="hidden" v-model="equipment.employee_id" />
                                        <div class="text-info">This equipment will automatically be assigned to you.</div>
                                    </template>

                                    <span v-if="errors.employee_id" class="text-danger">
                                        <span v-for="error in errors.employee_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div class="form-group row" v-if="equipment.employee_id">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right" :class="{'is-invalid': errors.jobsite_id}">Jobsite: <i class="fa fa-asterisk text-danger"></i></label>
                                <div class="col-sm-8">
                                    <jobsite-select v-on:vselected="onJobSiteSelectUpdate" :hasError="errors.jobsite_id"></jobsite-select>

                                    <span v-if="errors.jobsite_id" class="text-danger">
                                        <span v-for="error in errors.jobsite_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header text-muted">Dates</div>
                        <div class="card-body">
                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.next_maintenance_date }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">Next Maintenance Date:</label>
                                <div class="col-sm-8">
                                    <date-picker v-model="equipment.next_maintenance_date" @dp-change="datePickerChanged" :config="optionsCalibrate" placeholder="Select Next Maintenance Date"></date-picker>
                                    <span v-if="errors.next_maintenance_date" class="text-danger">
                                        <span v-for="error in errors.next_maintenance_date">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.calibrate_date }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">Last Calibration Date:</label>
                                <div class="col-sm-8">
                                    <date-picker v-model="equipment.calibrate_date" @dp-change="datePickerChanged" :config="optionsCalibrate" placeholder="Select Calibration Date"></date-picker>
                                    <span v-if="errors.calibrate_date" class="text-danger">
                                        <span v-for="error in errors.calibrate_date">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.issue_date }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">Issue Date:</label>
                                <div class="col-sm-8">
                                    <date-picker v-model="equipment.issue_date" @dp-change="datePickerChanged" :config="optionsIssue" placeholder="Select Issue Date"></date-picker>
                                    <span v-if="errors.issue_date" class="text-danger">
                                        <span v-for="error in errors.issue_date">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.return_date }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">Return Date:</label>
                                <div class="col-sm-8">
                                    <date-picker v-model="equipment.return_date" @dp-change="datePickerChanged" :config="optionsReturn" placeholder="Select Return Date"></date-picker>
                                    <span v-if="errors.return_date" class="text-danger">
                                        <span v-for="error in errors.return_date">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.purchase_date }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">Purchase Date:</label>
                                <div class="col-sm-8">
                                    <date-picker v-model="equipment.purchase_date" @dp-change="datePickerChanged" :config="optionsPurchase" placeholder="Select Purchase Date"></date-picker>
                                    <span v-if="errors.purchase_date" class="text-danger">
                                        <span v-for="error in errors.purchase_date">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <equipment-sections-builder v-if="customFieldsStructure.length > 0" :fields="customFieldsStructure" :initialValues="fields" @customFieldsChanged="setCustomFields" :errors="errors"></equipment-sections-builder>

                    <div class="card mb-3">
                        <div class="card-header text-muted">Notes &amp; Files/Images</div>
                        <div class="card-body">

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.equipment_notes }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">Equipment Notes:</label>
                                <div class="col-sm-8">
                                    <b-form-textarea v-model="equipment.equipment_notes" placeholder="(Optional). Enter any notes about this equipment." rows="3"></b-form-textarea>
                                </div>
                            </div>
                            <hr/>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.files }" class="row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">Files</label>
                                <div class="col-sm-8">

                                    <b-form-file v-model="files" class="mt-3" plain multiple></b-form-file>

                                    <span v-if="errors.files" class="text-danger">
                                        <span v-for="error in errors.files">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <hr/>
            <button type="button" class="btn btn-success" v-on:click="saveEquipment(this)" :disabled="saving || missingEmployeeId">
                <span v-if="equipment.equipment_id">Update</span>
                <span v-else>Create</span>
                Equipment<span v-if="missingEmployeeId"> - Missing Profile Employee Record</span>
            </button>
        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                missingJobsite: false,
                equipment_id: this.$route.params.id,
                equipment: {},
                divisions: [],
                files: [],
                fields: [],
                types: [],
                admins: [],
                full_types: {},
                errors: {},
                optionsCalibrate: {
                    format: 'MM/DD/YYYY',
                    date: ""
                },
                optionsIssue: {
                    format: 'MM/DD/YYYY',
                    date: ""
                },
                optionsReturn: {
                    format: 'MM/DD/YYYY',
                    date: ""
                },
                optionsPurchase: {
                    format: 'MM/DD/YYYY',
                    date: ""
                },
                datePicked: new Date(),
                userSelectValues: {},
            }
        },

        computed: {
            missingEmployeeId(){
                return !this.$root.hasPermission('equipment.manage') && !this.$store.state.user.EmployeeId;
            },

            // SiteSelectedValues(){
            //     //Check for null
            //     if(!this.equipment.jobsite_id || this.equipment.jobsite_id == null)
            //     {
            //         //Check the store
            //         if(this.$store.state.user.jobsite)
            //         {
            //             var jobsite = this.$store.state.user.jobsite;

            //             //Set the HSE jobsite
            //             this.equipment.jobsite_id = jobsite.JobsiteId;

            //             //Send it
            //             return {JobsiteId: jobsite.JobsiteId, JobsiteNumber: jobsite.JobsiteNumber, JobsiteName: jobsite.JobsiteName};
            //         }

            //         return null;
            //     }

            //     //Send it
            //     return {JobsiteId: this.equipment.jobsite_id, JobsiteNumber: this.equipment.JobsiteNumber, JobsiteName: this.equipment.JobsiteName};
            // },

            // equipmentUser(){
            //     //check for null
            //     if(!this.equipment.user_id)
            //     {
            //         return {};
            //     }

            //     return {UserId: this.equipment.user_id, FirstName: this.equipment.FirstName, LastName: this.equipment.LastName};
            // },
            customFieldsStructure() {
                if (!this.equipment.equipment_type_id)
                {
                    return [];
                }

                return JSON.parse(this.full_types[this.equipment.equipment_type_id].custom_fields) || [];
            },
        },

        methods: {
            setCustomFields: function(fields) {
                this.equipment.custom_fields = fields;
            },

            //Get the Various Resources We Need to Edit a Jobsite
            getErrorClass: function(attribute) {
                return {
                    'form-group has-error': this.errors[attribute],
                    'form-group': !this.errors[attribute],
                }
            },

            getEquipment() {
                //Check For Query Params
                var payload = {};

                //Check for queyr
                if(this.$route.query.clone_id)
                {
                    payload.clone_id = this.$route.query.clone_id;
                }

                //Get the data
                this.$http
                    .get('equipment/edit' + (this.equipment_id ? '/' + this.equipment_id : ''), payload)
                    .then(response => {
                        //Set all the data
                        this.equipment = response.data.equipment;
                        this.types = [{value: null, text: '-- Select Equipment Type --'}].concat(response.data.types);
                        this.full_types = response.data.full_types;
                        this.divisions = response.data.divisions;
                        this.fields = JSON.parse(this.equipment.custom_fields || '{}');
                        this.admins = response.data.admins;

                        //Set the dates. This is annoying, but its quick and works
                        this.optionsCalibrate.date = this.equipment.calibrate_date;
                        this.optionsIssue.date = this.equipment.issue_date;
                        this.optionsReturn.date = this.equipment.return_date;
                        this.optionsPurchase.date = this.equipment.purchase_date;

                        //Helper to fix missing jobsites
                        if(this.equipment.equipment_id && this.equipment.employee_id && !this.equipment.jobsite_id)
                        {
                            //Set it
                            this.missingJobsite = true;
                        }

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

            saveEquipment() {
                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                // This is just used for display, and probably API users
                // It's overriding the equipment_type_id when both are set, so unset it
                delete this.equipment.equipment_type_name;

                //Send it
                this.$http
                .postWithFiles('equipment/edit' + (this.equipment_id ? '/' + this.equipment_id : ''), this.equipment, this.files)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Equipment Saved');

                    //Return to main list?
                    this.$router.push('/equipment/view/' + response.data.equipment_id);
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },

            onJobSiteSelectUpdate (jobsite) {
                //Update the ID
                this.equipment.jobsite_id = jobsite.JobsiteId
                this.equipment.JobsiteName = jobsite.JobsiteName;
                this.equipment.JobsiteNumber = jobsite.JobsiteNumber;
            },

            // onUserSelectUpdate ( user ) {
            //     this.equipment.user_id = user.UserId;
            // },

            datePickerChanged ( data ) {
                // Stringify datePicked
                var dateParts = data.target.value.split("/");
                this.equipment.equipment_date = dateParts[2]+'-'+dateParts[0]+'-'+dateParts[1];
            },

            onEmployeeSelect(employee){
                //Update
                this.equipment.employee_id = employee.EmployeeId;

                //Force update?
                this.$forceUpdate();
            },
        },

        mounted() {
            //Get the Equipment
            this.getEquipment();
        }
    }
</script>
